// src/components/FrameworkWheel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BRAND } from "../config/brandConfig";

/** ---------- Content (Definitive Source of Truth) ---------- */
// Export STEPS so FrameworkDetailPanel (now inline in HomePage) can import it.
export const STEPS = [
  {
    id: 1,
    name: "Clarify",
    subtitle: "Define goals & real-life constraints.",
    icon: "🧭",
    sessionBullets: [
      "Map goals, life transitions, business realities, and cash flow needs.",
    ],
    outcomeBullets: [
      "A clear picture of your current life architecture: people, entities, cash flows.",
      "A short list of 3–5 governing priorities that everything else will answer to.",
      "Agreement on what success looks like 10+ years from now.",
    ],
  },
  {
    id: 2,
    name: "Diagnose",
    subtitle: "Analyze architecture & risk profile.",
    icon: "🔬",
    sessionBullets: [
      "X-ray where risk and concentration actually live: portfolios, business, taxes, guarantees.",
      "Surface hidden frictions: fees, complexity, behavioral triggers, mismatched time horizons.",
      "Stress-test the current setup against a few simple “what if” shocks.",
    ],
    outcomeBullets: [
      "A risk map: where your plan is robust, and where it’s exposed.",
      "A short list of “critical issues” that must be addressed before anything else.",
      "A shared understanding of tradeoffs: where you’re intentionally taking risk, and why.",
    ],
  },
  {
    id: 3,
    name: "Design",
    subtitle: "Build strategies for purpose.",
    icon: "📐",
    sessionBullets: [
      "Translate your priorities into design rules: liquidity, growth, safety, and flexibility.",
      "Decide which “buckets” exist (family, business, legacy, optionalality) and how they talk to each other.",
      "Balance portfolios, entities, and protections around the life you actually want to live.",
    ],
    outcomeBullets: [
      "A draft Wealth Design Blueprint that connects accounts, entities, and goals.",
      "Clear target ranges for cash, risk assets, and “never lose” capital.",
      "A list of implementation steps, ordered by impact and urgency.",
    ],
  },
  {
    id: 4,
    name: "Deploy",
    subtitle: "Execute with discipline.",
    icon: "⚙️",
    sessionBullets: [
      "Turn the design into specific actions: transfers, allocations, account changes, and documents.",
      "Decide what will be automated (savings, rebalancing, distributions) vs. what stays manual and deliberate.",
      "Script behavior ahead of time so decisions in volatile moments are already made.",
    ],
    outcomeBullets: [
      "An implementation checklist with owners and timelines (who does what, and when).",
      "Standing instructions: how cash moves, when rebalancing happens, how we respond to volatility.",
      "Clarity on your communication rhythm with us during and after deployment.",
    ],
  },
  {
    id: 5,
    name: "Review",
    subtitle: "Adapt to life, not noise.",
    icon: "🔄",
    sessionBullets: [
      "Compare what actually happened vs. what we planned for: life events, markets, spending, business.",
      "Decide whether any of the original principles need to change—or if the plan is working as designed.",
      "Re-prioritize: what’s coming in the next 12–24 months that the plan should already be preparing for?",
    ],
    outcomeBullets: [
      "A concise Annual Review Summary: what changed, what we adjusted, what stays the same.",
      "Updated action items (if any) and confirmed guardrails for the year ahead.",
      "Confidence that the plan is alive, monitored, and aligned with your current reality.",
    ],
  },
];

/** ---------- Core Utils & Tokens ---------- */
const toRad = (d) => (d * Math.PI) / 180; // FIX: toRad defined before use.

const TOK = {
  ink: BRAND.colors?.ink || BRAND.colors?.textDark || "#0B1A3C",
  ink70: BRAND.colors?.ink70 || BRAND.colors?.textMedium || "#667085",
  line: BRAND.colors?.line || BRAND.colors?.border || "#E5E7EB",
  brand: BRAND.colors?.brand || BRAND.colors?.accent || "#0B3C5D",
  card: BRAND.colors?.card || BRAND.colors?.panel || "#FFFFFF",
  bg: BRAND.colors?.bg || BRAND.colors?.background || "#F7F8FA",
  shadowXs: BRAND.shadows?.xs || "0 1px 2px rgba(0,0,0,.06)",
  shadowSm: BRAND.shadows?.sm || "0 2px 6px rgba(0,0,0,.10)",
};

const MOTION = {
  fast: BRAND.motion?.fast || "150ms",
  ease: BRAND.motion?.ease || "cubic-bezier(.2,.8,.2,1)",
  press: BRAND.motion?.activePress || "translateY(1px)",
};

/** ---------- Small Chips & Legend (Functional components) ---------- */
function ChipButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        height: 32,
        padding: "0 12px",
        borderRadius: 999,
        background: "transparent",
        border: `1px solid ${TOK.line}`,
        color: TOK.ink,
        cursor: "pointer",
        transition: `transform ${MOTION.fast} ${MOTION.ease}, background-color ${MOTION.fast} ${MOTION.ease}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TOK.bg)}
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = "transparent")
      }
      onMouseDown={(e) => (e.currentTarget.style.transform = MOTION.press)}
      onMouseUp={(e) => (e.currentTarget.style.transform = "translateY(0)")}
      onFocus={(e) => {
        e.currentTarget.style.outline = "3px solid rgba(11,60,93,.40)";
        e.currentTarget.style.outlineOffset = "2px";
      }}
      onBlur={(e) => (e.currentTarget.style.outline = "none")}
    >
      {label}
    </button>
  );
}

function StepLegend({ steps, activeIdx, onSelect }) {
  return (
    <ol
      aria-label="All framework steps"
      role="listbox"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 8,
        marginTop: 16,
      }}
    >
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        return (
          <li key={s.id} role="option" aria-selected={isActive}>
            <button
              onClick={() => onSelect(i)}
              aria-current={isActive ? "true" : undefined}
              aria-label={`Go to step ${s.id}: ${s.name}`}
              style={{
                width: "100%",
                textAlign: "left",
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 12,
                border: `1px solid ${isActive ? TOK.brand : TOK.line}`,
                background: isActive ? "rgba(11,60,93,0.06)" : "transparent",
                color: TOK.ink,
                cursor: "pointer",
                outline: "none",
                transition: `background-color ${MOTION.fast} ${MOTION.ease}, border-color ${MOTION.fast} ${MOTION.ease}`,
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = "3px solid rgba(11,60,93,.40)";
                e.currentTarget.style.outlineOffset = "2px";
              }}
              onBlur={(e) => (e.currentTarget.style.outline = "none")}
            >
              <span
                style={{
                  minWidth: 26,
                  height: 26,
                  borderRadius: 999,
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 700,
                  fontSize: 13,
                  color: isActive ? "#fff" : TOK.ink,
                  background: isActive ? TOK.brand : "rgba(0,0,0,0.04)",
                  border: `1px solid ${isActive ? TOK.brand : TOK.line}`,
                }}
              >
                {s.id}
              </span>
              <span>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                <div
                  style={{ color: TOK.ink70, fontSize: 12, lineHeight: "16px" }}
                >
                  {s.subtitle}
                </div>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** ---------- Main Framework Wheel Component ---------- */
// This component now only renders the left column (Wheel and Legend)
export default function FrameworkWheel({ setActiveStepIndex }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const nodesRef = useRef([]);

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Propagate active state up to HomePage/SeaStatePanel
  useEffect(() => {
    if (setActiveStepIndex) {
      // Pass the step index (0-4)
      setActiveStepIndex(activeIdx);
    }
  }, [activeIdx, setActiveStepIndex]);

  // keep active at 12 o’clock
  const rotation = useMemo(() => -(activeIdx * 72), [activeIdx]); // 360/5 = 72°
  const innerRadius = 120; // spoke inner end
  const outerRadius = 150; // handle tip

  // pre-rotated positions for 5 handles
  const handlePositions = useMemo(() => {
    return STEPS.map((_, idx) => {
      const baseDeg = -90 + idx * 72; // -90 puts step 1 at top
      const a = toRad(baseDeg + rotation);
      const x = outerRadius * Math.cos(a);
      const y = outerRadius * Math.sin(a);
      const sx = innerRadius * Math.cos(a);
      const sy = innerRadius * Math.sin(a);
      const deg = baseDeg + rotation;
      return { x, y, sx, sy, deg };
    });
  }, [rotation]);

  // roving focus to active
  useEffect(() => {
    nodesRef.current[activeIdx]?.focus?.();
  }, [activeIdx]);

  const next = () => setActiveIdx((i) => (i + 1) % STEPS.length);
  const prev = () => setActiveIdx((i) => (i - 1 + STEPS.length) % STEPS.length);

  const onKeyDown = (e) => {
    if (["ArrowRight", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      next();
    }
    if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      prev();
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    }
    if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(STEPS.length - 1);
    }
  };

  return (
    <section aria-labelledby="framework-title-section" style={{ padding: 0 }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        {/* Wheel + detail */}
        <div
          style={{
            // Note: The parent (HomePage) manages the 2-column layout. This div renders the LEFT side only.
            display: "grid",
            gridTemplateColumns: "1fr", // Force single column rendering here
            gap: "2rem",
            alignItems: "start",
          }}
        >
          {/* Wheel Card (Left) */}
          <div
            role="radiogroup"
            aria-label="Framework steps"
            onKeyDown={onKeyDown}
            tabIndex={0}
            style={{
              borderRadius: 20,
              background: TOK.card,
              border: `1px solid ${TOK.line}`,
              boxShadow: TOK.shadowSm,
              padding: 20,
            }}
          >
            <div
              style={{
                position: "relative",
                width: 360,
                height: 460,
                margin: "0 auto",
              }}
            >
              {/* Helm SVG and Handles/Clickspots */}
              <svg
                width="360"
                height="360"
                viewBox="-180 -180 360 360"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transform: `rotate(${rotation}deg)`,
                  transition: prefersReduced
                    ? "none"
                    : `transform ${MOTION.fast} ${MOTION.ease}`,
                }}
                aria-hidden="true"
              >
                {/* Rims */}
                <circle
                  cx="0"
                  cy="0"
                  r="136"
                  fill={TOK.card}
                  stroke={TOK.line}
                  strokeWidth="8"
                />
                <circle
                  cx="0"
                  cy="0"
                  r="88"
                  fill={TOK.card}
                  stroke={TOK.line}
                  strokeWidth="4"
                />
                <circle
                  cx="0"
                  cy="0"
                  r="72"
                  fill="none"
                  stroke={TOK.line}
                  strokeWidth="1"
                  opacity="0.5"
                />

                {/* Spokes */}
                {handlePositions.map((p, i) => (
                  <line
                    key={`spoke-${i}`}
                    x1={p.sx}
                    y1={p.sy}
                    x2={p.x * 0.86}
                    y2={p.y * 0.86}
                    stroke={TOK.line}
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                ))}

                {/* Handles */}
                {handlePositions.map((p, i) => {
                  const isActive = i === activeIdx;
                  const w = isActive ? 44 : 38;
                  const h = isActive ? 18 : 16;
                  return (
                    <g
                      key={`handle-${i}`}
                      transform={`translate(${p.x},${p.y}) rotate(${p.deg})`}
                    >
                      <rect
                        x={-w / 2}
                        y={-h / 2}
                        rx={9}
                        ry={9}
                        width={w}
                        height={h}
                        fill={isActive ? TOK.brand : "rgba(205,170,125,.85)"}
                        stroke={isActive ? TOK.brand : "rgba(180,140,95,.9)"}
                        strokeWidth="1.5"
                      />
                    </g>
                  );
                })}

                {/* Hub */}
                <circle
                  cx="0"
                  cy="0"
                  r="34"
                  fill={TOK.card}
                  stroke={TOK.line}
                  strokeWidth="3"
                />
                <circle cx="0" cy="0" r="8" fill={TOK.line} />
              </svg>

              {/* Click hotspots over each handle (Transparent buttons for interaction) */}
              {STEPS.map((s, i) => {
                const pos = handlePositions[i];
                const isActive = i === activeIdx;
                return (
                  <button
                    key={s.id}
                    ref={(el) => (nodesRef.current[i] = el)}
                    role="radio"
                    aria-checked={isActive}
                    tabIndex={isActive ? 0 : -1}
                    title={`${s.id}. ${s.name}`}
                    onClick={() => setActiveIdx(i)}
                    onMouseDown={(e) => {
                      e.currentTarget.style.transform = `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) ${MOTION.press}`;
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px)`;
                    }}
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "180px", // center of SVG in the card
                      transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px)`,
                      width: 56,
                      height: 56,
                      borderRadius: 999,
                      background: "transparent",
                      border: "1px solid transparent",
                      boxShadow: "none",
                      cursor: "pointer",
                      outline: "none",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline =
                        "3px solid rgba(11,60,93,.40)";
                      e.currentTarget.style.outlineOffset = "2px";
                    }}
                    onBlur={(e) => (e.currentTarget.style.outline = "none")}
                  />
                );
              })}

              {/* Center label (fixed, calmer weight) */}
              <div
                style={{
                  position: "absolute",
                  top: 180,
                  left: 180,
                  transform: "translate(-50%, -50%)",
                  width: 120,
                  height: 120,
                  borderRadius: 999,
                  background: TOK.card,
                  border: `1px solid ${TOK.line}`,
                  boxShadow: TOK.shadowXs,
                  display: "grid",
                  placeItems: "center",
                  textAlign: "center",
                  padding: "0 10px",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: TOK.ink70,
                    letterSpacing: ".08em",
                    fontWeight: 700,
                  }}
                >
                  ENTER
                </div>
                <div style={{ fontSize: 14, color: TOK.ink, fontWeight: 600 }}>
                  {STEPS[activeIdx].name}
                </div>
              </div>

              {/* Prev / Next controls */}
              <div
                style={{
                  position: "absolute",
                  left: 12,
                  right: 12,
                  bottom: 8,
                  display: "flex",
                  justifyContent: "space-between",
                }}
                aria-hidden="true"
              >
                <ChipButton label="Prev" onClick={prev} />
                <ChipButton label="Next" onClick={next} />
              </div>
            </div>

            {/* Legend */}
            <StepLegend
              steps={STEPS}
              activeIdx={activeIdx}
              onSelect={setActiveIdx}
            />
          </div>
          {/* Removed Detail Panel and Sea-State Panel rendering from here */}
        </div>
      </div>
    </section>
  );
}
